import { useEffect, useMemo, useState } from 'react';
import { listTemplatesMeta, type TemplateMeta } from '../api/templates';
import { filterPublicGalleryTemplates } from '../utils/publicTemplateGallery';

export type TemplateCategory = 'business' | 'artist';

function normalizeCategory(raw: unknown): TemplateCategory | null {
  const c = String(raw || '').toLowerCase();
  if (c === 'business' || c.includes('business')) return 'business';
  if (c === 'artist' || c.includes('artist')) return 'artist';
  return null;
}

function isPublicTemplate(meta: TemplateMeta): boolean {
  if (meta.active === false) return false;
  if (meta.approved === false) return false;
  const status = String((meta as any).approvalStatus || '').toLowerCase();
  if (status === 'rejected') return false;
  return true;
}

export function usePublicTemplates(category?: TemplateCategory | 'all') {
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { approvedOnly: true, activeOnly: true } as const;
        const list = await listTemplatesMeta(params);
        if (!active) return;
        const filtered = filterPublicGalleryTemplates((list || []).filter(isPublicTemplate));
        setTemplates(filtered);
        try {
          const existing = JSON.parse(localStorage.getItem('templates_cache_meta') || '{}');
          const merged = { ...existing };
          filtered.forEach((m) => {
            if (m?.id) merged[m.id] = m;
          });
          localStorage.setItem('templates_cache_meta', JSON.stringify(merged));
        } catch {}
      } catch (e: any) {
        if (!active) return;
        let cached: TemplateMeta[] = [];
        try {
          const raw = localStorage.getItem('templates_cache_meta');
          if (raw) cached = Object.values(JSON.parse(raw)).filter(Boolean) as TemplateMeta[];
        } catch {}
        if (cached.length) {
          setTemplates(filterPublicGalleryTemplates(cached.filter(isPublicTemplate)));
        } else {
          setError(e?.message || 'Failed to load templates');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const byCategory = useMemo(() => {
    const business: TemplateMeta[] = [];
    const artist: TemplateMeta[] = [];
    templates.forEach((t) => {
      const cat = normalizeCategory(t.category);
      if (cat === 'artist') artist.push(t);
      else business.push(t);
    });
    return { business, artist, all: templates };
  }, [templates]);

  const visible = useMemo(() => {
    if (!category || category === 'all') return byCategory.all;
    return category === 'artist' ? byCategory.artist : byCategory.business;
  }, [byCategory, category]);

  const featured = useMemo(() => {
    return byCategory.business[0] || byCategory.artist[0] || templates[0] || null;
  }, [byCategory, templates]);

  return { templates: visible, allTemplates: templates, byCategory, featured, loading, error };
}
